/**
 * =============================================================================
 * PAYROLL REPORTS API SERVICE
 * Frontend service for calling backend payroll reports API endpoints
 * =============================================================================
 */

import { getHeaders } from '../utility/getHeader';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4402/api';
const REPORTS_BASE_URL = `${API_BASE_URL}/payroll/reports`;

const normalizeBaseUrl = (value) => value.replace(/\/+$/, '');

/**
 * Get authorization headers for API requests
 */
const getAuthHeaders = () => {
  const authHeaders = getHeaders();
  return {
    'Content-Type': 'application/json',
    ...authHeaders.headers
  };
};

/**
 * Generic API request handler
 */
const apiRequest = async (url, options = {}) => {
  try {
    console.log('API Request:', url);
    const response = await fetch(url, {
      headers: getAuthHeaders(),
      ...options
    });
    
    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('API Request Error for URL:', url, error);
    throw error;
  }
};

/**
 * Verify whether the backend host is reachable before firing multiple report requests.
 * A 401/404 still means the server is up; only network failures should return false.
 */
export const checkPayrollApiConnection = async () => {
  const healthUrl = `${normalizeBaseUrl(API_BASE_URL)}/test`;

  try {
    const response = await fetch(healthUrl, {
      method: 'GET',
      headers: getAuthHeaders()
    });

    return response.ok || response.status === 401 || response.status === 404;
  } catch (error) {
    console.error('Payroll API connection check failed:', healthUrl, error);
    return false;
  }
};

/**
 * =============================================================================
 * PAYROLL REPORTS API FUNCTIONS
 * =============================================================================
 */

/**
 * 1. Monthly Payroll Report
 * GET /api/payroll/reports/monthly?year=2024&month=12&department_id=1&batch_id=1
 */
export const getMonthlyPayrollReport = async (year, month, departmentId = null, batchId = null) => {
  const params = new URLSearchParams({
    year: year.toString(),
    month: month.toString()
  });
  
  if (departmentId) {
    params.append('department_id', departmentId.toString());
  }
  
  if (batchId) {
    params.append('batch_id', batchId.toString());
  }
  
  console.log(`${REPORTS_BASE_URL}/monthly?${params}`);
  return await apiRequest(`${REPORTS_BASE_URL}/monthly?${params}`);
};

/**
 * 2. Yearly Payroll Summary
 * GET /api/payroll/reports/yearly?year=2024&department_id=1
 */
export const getYearlyPayrollSummary = async (year, departmentId = null) => {
  const params = new URLSearchParams({
    year: year.toString()
  });
  
  if (departmentId) {
    params.append('department_id', departmentId.toString());
  }
  
  const url = `${REPORTS_BASE_URL}/yearly?${params}`;
  console.log('getYearlyPayrollSummary URL:', url);
  return await apiRequest(url);
};

/**
 * 3. Department-wise Report
 * GET /api/payroll/reports/departmental?department_id=1&start_date=2024-01-01&end_date=2024-12-31
 */
export const getDepartmentalReport = async (departmentId, startDate, endDate) => {
  const params = new URLSearchParams({
    department_id: departmentId.toString(),
    start_date: startDate,
    end_date: endDate
  });
  
  const url = `${REPORTS_BASE_URL}/departmental?${params}`;
  console.log('getDepartmentalReport URL:', url);
  return await apiRequest(url);
};

/**
 * 4. Position-wise Analysis
 * GET /api/payroll/reports/position?department_id=1&start_date=2024-01-01&end_date=2024-12-31
 */
export const getPositionWiseAnalysis = async (departmentId = null, startDate, endDate) => {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate
  });
  
  if (departmentId) {
    params.append('department_id', departmentId.toString());
  }
  
  const url = `${REPORTS_BASE_URL}/position?${params}`;
  console.log('getPositionWiseAnalysis URL:', url);
  return await apiRequest(url);
};

/**
 * 5. Comparative Analysis (Month-over-Month)
 * GET /api/payroll/reports/comparative?start_date=2024-01-01&end_date=2024-12-31&department_id=1
 */
export const getComparativeAnalysis = async (startDate, endDate, departmentId = null) => {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate
  });
  
  if (departmentId) {
    params.append('department_id', departmentId.toString());
  }
  
  return await apiRequest(`${REPORTS_BASE_URL}/comparative?${params}`);
};

/**
 * 6. Payroll Trends Analysis (Last 12 Months)
 * GET /api/payroll/reports/trends?department_id=1
 */
export const getPayrollTrends = async (departmentId = null) => {
  const params = new URLSearchParams();
  
  if (departmentId) {
    params.append('department_id', departmentId.toString());
  }
  
  const queryString = params.toString();
  const url = queryString ? `${REPORTS_BASE_URL}/trends?${queryString}` : `${REPORTS_BASE_URL}/trends`;
  
  console.log('getPayrollTrends URL:', url);
  return await apiRequest(url);
};

/**
 * 7. Individual Employee Report
 * GET /api/payroll/reports/employee?employee_id=1&start_date=2024-01-01&end_date=2024-12-31
 */
export const getEmployeeReport = async (employeeId, startDate, endDate) => {
  const params = new URLSearchParams({
    employee_id: employeeId.toString(),
    start_date: startDate,
    end_date: endDate
  });
  
  return await apiRequest(`${REPORTS_BASE_URL}/employee?${params}`);
};

/**
 * 8. Summary Report for Dashboard
 * GET /api/payroll/reports/summary
 */
export const getSummaryReport = async () => {
  const url = `${REPORTS_BASE_URL}/summary`;
  console.log('getSummaryReport URL:', url);
  return await apiRequest(url);
};

/**
 * 9. Deductions Analysis Report
 * GET /api/payroll/reports/deductions?department_id=1&start_date=2024-01-01&end_date=2024-12-31
 */
export const getDeductionsAnalysis = async (departmentId = null, startDate, endDate) => {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate
  });
  
  if (departmentId) {
    params.append('department_id', departmentId.toString());
  }
  
  return await apiRequest(`${REPORTS_BASE_URL}/deductions?${params}`);
};

/**
 * 10. Attendance-based Report
 * GET /api/payroll/reports/attendance?department_id=1&start_date=2024-01-01&end_date=2024-12-31
 */
export const getAttendanceReport = async (departmentId = null, startDate, endDate) => {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate
  });
  
  if (departmentId) {
    params.append('department_id', departmentId.toString());
  }
  
  return await apiRequest(`${REPORTS_BASE_URL}/attendance?${params}`);
};

/**
 * 11. Get Employees with Department and Position Details
 * GET /api/payroll/reports/employees?employment_status=Active&department_id=1&position_id=1
 */
export const getEmployeesWithDetails = async (employmentStatus = 'Active', departmentId = null, positionId = null) => {
  const params = new URLSearchParams();
  
  if (employmentStatus) {
    params.append('employment_status', employmentStatus);
  }
  
  if (departmentId) {
    params.append('department_id', departmentId.toString());
  }
  
  if (positionId) {
    params.append('position_id', positionId.toString());
  }
  
  const queryString = params.toString();
  const url = queryString ? `${REPORTS_BASE_URL}/employees?${queryString}` : `${REPORTS_BASE_URL}/employees`;
  
  return await apiRequest(url);
};

/**
 * 12. Get Payroll Batches by Month
 * GET /api/payroll/batches?payroll_month=2025-08&department_id=1
 */
export const getPayrollBatches = async (payrollMonth = null, departmentId = null) => {
  const params = new URLSearchParams();
  
  if (payrollMonth) {
    params.append('payroll_month', payrollMonth);
  }
  
  if (departmentId) {
    params.append('department_id', departmentId.toString());
  }
  
  const queryString = params.toString();
  const url = queryString ? `${API_BASE_URL}/payroll/batches?${queryString}` : `${API_BASE_URL}/payroll/batches`;
  
  console.log('Calling Payroll Batches API:', url);
  return await apiRequest(url);
};

/**
 * =============================================================================
 * UTILITY FUNCTIONS
 * =============================================================================
 */

/**
 * Format date for API requests (YYYY-MM-DD)
 */
export const formatDateForAPI = (year, month, day = 1) => {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

/**
 * Get month range for API requests
 */
export const getMonthDateRange = (year, month) => {
  const startDate = formatDateForAPI(year, month, 1);
  const lastDay = new Date(year, month, 0).getDate();
  const endDate = formatDateForAPI(year, month, lastDay);
  
  return { startDate, endDate };
};

/**
 * Get year date range for API requests
 */
export const getYearDateRange = (year) => {
  const startDate = formatDateForAPI(year, 1, 1);
  const endDate = formatDateForAPI(year, 12, 31);
  
  return { startDate, endDate };
};

/**
 * Transform backend response to match frontend expectations
 */
export const transformReportData = (backendData, reportType) => {
  if (!backendData || !backendData.data) {
    return [];
  }
  
  const records = backendData.data.records || backendData.data || [];
  
  return records.map(record => ({
    id: record.id || record.employee_id,
    employeeId: record.employee_number || record.employee_id || `EMP${String(record.id).padStart(3, '0')}`,
    name: record.employee_name || `${record.first_name || ''} ${record.last_name || ''}`.trim(),
    department: record.department_name || record.department,
    position: record.position || record.position_name,
    basicSalary: parseFloat(record.monthly_salary || record.basic_salary || 0),
    allowances: parseFloat(record.allowances || record.other_income || 0),
    overtime: parseFloat(record.overtime || 0),
    grossSalary: parseFloat(record.gross_salary || 0),
    incomeTax: parseFloat(record.income_tax || 0),
    socialSecurity: parseFloat(record.sso_deduction || record.social_security || 0),
    providentFund: parseFloat(record.provident_fund || record.advance_deduction || 0),
    totalDeductions: parseFloat(record.total_deductions || 0),
    netSalary: parseFloat(record.net_salary || 0),
    workingDays: record.total_days_in_month || record.working_days || 30,
    actualWorkingDays: record.work_days || record.actual_work_days || 0,
    month: record.month || new Date().toLocaleString('default', { month: 'long' }),
    year: record.year || new Date().getFullYear(),
    nationalId: record.national_id || record.thai_id_number || `1${Math.floor(Math.random() * 9999999999999).toString().padStart(12, '0')}`,
    bankAccount: record.bank_account || record.bank_account_number || `${Math.floor(Math.random() * 9999999999).toString().padStart(10, '0')}`,
    address: record.address || record.current_address || 'Bangkok, Thailand'
  }));
};

const payrollReportsApi = {
  getMonthlyPayrollReport,
  getYearlyPayrollSummary,
  getDepartmentalReport,
  getPositionWiseAnalysis,
  getComparativeAnalysis,
  getPayrollTrends,
  getEmployeeReport,
  getSummaryReport,
  getDeductionsAnalysis,
  getAttendanceReport,
  getEmployeesWithDetails,
  getPayrollBatches,
  formatDateForAPI,
  getMonthDateRange,
  getYearDateRange,
  transformReportData
};

export default payrollReportsApi;
